import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ConfirmationModalService } from '@epicuro-next/ataraxia/modal';
import {
  APP_PRIVATE_PATHS,
  APP_TOP_LEVEL_PATH,
} from '@epicuro-next/navigation/constants';
import {
  BROWSER_STORAGE_SERVICE,
  BROWSER_STORAGE_TOKEN,
} from '@epicuro-next/platform/storage';
import { setActiveAccount } from '@epicuro-next/state/accounts-state';
import { selectPendingBasketsAlertRule } from '@epicuro-next/state/basket-state';
import { go, selectRouteParams } from '@epicuro-next/state/router-store';
import { selectSiteId } from '@epicuro-next/state/session-state';
import { selectUser } from '@epicuro-next/state/user-state';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { BasketStatus } from 'graphql-api/gql-api-interfaces';
import { EMPTY, race, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

import { PendingBasketsAlertService } from '../data/pending-baskets-alert.service';

export const PENDING_BASKETS_ALERT_TITLE = $localize`:@@pendingBasketsAlertTitle:Pending Baskets Alert`;
export const PENDING_BASKET_ALERT_TITLE = $localize`:@@pendingBasketAlertTitle:Pending Basket Alert`;
export const PENDING_BASKETS_ALERT_MESSAGE = (userName: string) =>
  $localize`:@@pendingBasketsAlertMessage:Hi ${userName}, there are Pending baskets assigned to you that haven’t been approved for more than 24 hours.`;

export const PENDING_SINGLE_BASKET_ALERT_MESSAGE = (userName: string) =>
  $localize`:@@pendingSingleBasketAlertMessage:Hi ${userName}, there is a Pending basket assigned to you that haven’t been approved for more than 24 hours.`;

export const PENDING_BASKETS_CONFIRM_BUTTON = $localize`:@@pendingBasketsAlertConfirmButton:Go to Basket Manager`;
export const PENDING_SINGLE_BASKET_CONFIRM_BUTTON = $localize`:@@pendingSingleBasketAlertConfirmButton:Go to Basket`;

const PENDING_BASKETS_ALERT_KEY = 'pendingBasketsAlertTimestamp';

@Injectable()
export class PendingBasketsAlertEffects {
  private modalIsOpen = false;

  public initPendingBasketsAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveAccount),
      switchMap(() =>
        this.store.pipe(select(selectSiteId), filter(isNonNullish), take(1)),
      ),
      // wait until navigation is done
      switchMap(() =>
        race(
          timer(5000),
          this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            filter(({ urlAfterRedirects }: NavigationEnd) => {
              return [
                APP_TOP_LEVEL_PATH.accountSelector.path,
                APP_TOP_LEVEL_PATH.siteSelector.path,
                APP_TOP_LEVEL_PATH.userTerms.path,
                APP_TOP_LEVEL_PATH.offboarding.path,
                APP_TOP_LEVEL_PATH.urlResolver.path,
              ].every((url) => {
                return !urlAfterRedirects.includes(url);
              });
            }),
            take(1),
          ),
        ),
      ),
      switchMap(() => {
        return this.store.pipe(
          select(selectPendingBasketsAlertRule),
          switchMap((rule: { delay: number }) => {
            if (isNullish(rule) || this.modalIsOpen) return EMPTY;

            const lastShown = this.getLastShownTimestamp();
            const now = Date.now();
            const hoursSinceLastShown = lastShown
              ? (now - lastShown) / (1000 * 60 * 60)
              : Infinity;

            if (hoursSinceLastShown < rule.delay) {
              return EMPTY;
            }

            this.modalIsOpen = true;

            return this.pendingBasketsAlertService.checkPendingBaskets().pipe(
              concatLatestFrom(() => [
                this.store.select(selectUser),
                this.store.select(selectRouteParams),
              ]),
              switchMap(([pendingBaskets, user, params]) => {
                const hasPending = pendingBaskets?.length > 0;
                if (!hasPending) {
                  this.modalIsOpen = false;
                  return EMPTY;
                }

                const onlyOnePending = pendingBaskets.length === 1;

                if (
                  pendingBaskets.length > 1 &&
                  this.router.url.includes(APP_PRIVATE_PATHS.basketManager.path)
                ) {
                  this.modalIsOpen = false;
                  return EMPTY;
                }

                if (
                  onlyOnePending &&
                  this.router.url.includes(APP_PRIVATE_PATHS.basket.path) &&
                  pendingBaskets[0] === params.basketId
                ) {
                  this.modalIsOpen = false;
                  return EMPTY;
                }

                return this.confirmationModalService
                  .open({
                    message: onlyOnePending
                      ? PENDING_SINGLE_BASKET_ALERT_MESSAGE(
                          user?.displayName.split(' ')[0] ?? '',
                        )
                      : PENDING_BASKETS_ALERT_MESSAGE(
                          user?.displayName.split(' ')[0] ?? '',
                        ),
                    title: onlyOnePending
                      ? PENDING_BASKET_ALERT_TITLE
                      : PENDING_BASKETS_ALERT_TITLE,
                    showCloseBtn: true,
                    showCancelButton: false,
                    confirmButtonText: onlyOnePending
                      ? PENDING_SINGLE_BASKET_CONFIRM_BUTTON
                      : PENDING_BASKETS_CONFIRM_BUTTON,
                    options: { attachBrowserHistory: false },
                  })
                  .pipe(
                    map(() => {
                      this.saveShownTimestamp(now);
                      this.modalIsOpen = false;
                      if (onlyOnePending) {
                        return go({
                          commands: [
                            APP_PRIVATE_PATHS.basket.path,
                            pendingBaskets[0],
                          ],
                        });
                      }

                      const preselect = JSON.stringify({
                        status: BasketStatus.Pending,
                        user: undefined,
                        assignee: user?.userId,
                      });

                      return go({
                        commands: [APP_PRIVATE_PATHS.basketManager.path],
                        extras: {
                          queryParams: {
                            preselect,
                          },
                        },
                      });
                    }),
                    catchError(() => {
                      this.modalIsOpen = false;
                      return EMPTY;
                    }),
                  );
              }),
              catchError(() => {
                this.modalIsOpen = false;
                return EMPTY;
              }),
            );
          }),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private pendingBasketsAlertService: PendingBasketsAlertService,
    private confirmationModalService: ConfirmationModalService,
    @Inject(BROWSER_STORAGE_TOKEN)
    private storageService: BROWSER_STORAGE_SERVICE,
    private router: Router,
  ) {}

  private getLastShownTimestamp(): number | null {
    return this.storageService.get(PENDING_BASKETS_ALERT_KEY) ?? null;
  }

  private saveShownTimestamp(timestamp: number): void {
    this.storageService.set(PENDING_BASKETS_ALERT_KEY, timestamp);
  }
}
